/**
 * Separação empresa × pessoal.
 *
 * O Actual não tem coluna de tag na transação: a tag é o texto `#empresa`
 * escrito no campo `notes`, delimitado por espaço ou por outra cerquilha
 * (ver loot-core/src/shared/tags.ts).
 *
 * REGRA: nota sem a tag é PESSOAL. O padrão é explícito de propósito —
 * esquecer de marcar uma despesa da operação não pode inflar o "livre pra decidir".
 *
 * ⚠️ Quem construir o gesto de marcar `#empresa` (botão/toggle, tarefa futura) tem
 * que inserir a tag DELIMITADA (precedida de espaço/início e seguida de espaço/fim/
 * outra cerquilha) — é ali que o risco de "pontuação colada" (ver `PADRAO_EMPRESA`
 * abaixo) se fecha de verdade, não mexendo nesta regex.
 */

export type Escopo = 'pessoal' | 'empresa' | 'tudo';

/**
 * ⭐ Revisão final da branch (22/09/2026) — rótulo cru (em português, minúsculo)
 * de cada escopo, pra telas que precisam DIZER em que escopo o dono está
 * (`/medias`, `/compromissos`: hoje fixam `'pessoal'` sem avisar nada disso na
 * tela — ver os dois `data-testid="...-periodo"`). Cru de propósito, como
 * `ROTULOS` em `tiposDeGasto.ts`/`ajustes/TiposDeGasto.tsx`: quem exibe passa
 * por `t()` na hora de renderizar.
 */
export const ROTULO_ESCOPO: Record<Escopo, string> = {
  pessoal: 'pessoal',
  empresa: 'empresa',
  tudo: 'tudo',
};

export const TAG_EMPRESA = '#empresa';

/**
 * A tag termina no fim da string, num espaço, ou em outra cerquilha — e NÃO pode vir
 * precedida de outra cerquilha (`(?<!#)`): no vocabulário do Actual, `##empresa` é a
 * forma ESCAPADA/literal de "empresa", não a tag `#empresa` (ver `renameTagInNotes`
 * em loot-core/src/shared/tags.ts — `##Food` nunca é renomeado pela função deles,
 * exatamente porque `##` escapa a tag).
 *
 * De propósito, `#empresa.` e `#empresa,` NÃO casam (o `.`/`,` não é delimitador) —
 * fiel ao `parseTagsInWord` do Actual, onde pontuação colada faz parte do nome da
 * tag (`#empresa.` é a tag "empresa.", outra tag). Casar aqui criaria duas verdades
 * sobre a mesma transação: a nossa regra contaria como #empresa uma nota que a busca
 * nativa do Actual por `#empresa` não encontra.
 */
const PADRAO_EMPRESA = /(?<!#)#empresa(?=$|\s|#)/;

export function temTagEmpresa(notes: string | null | undefined): boolean {
  if (!notes) return false;
  return PADRAO_EMPRESA.test(notes);
}

export function ehDoEscopo(
  notes: string | null | undefined,
  escopo: Escopo,
): boolean {
  if (escopo === 'tudo') return true;
  const daEmpresa = temTagEmpresa(notes);
  return escopo === 'empresa' ? daEmpresa : !daEmpresa;
}
